'use client';

import { useActionState } from 'react';
import { sendContactEmail } from '@/app/actions/contact-actions';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';

const initialState = {
    success: false,
    message: '',
    errors: {}
};

export default function ContactForm() {
    const [state, formAction, isPending] = useActionState(sendContactEmail, initialState);

    return (
        <form action={formAction} className="space-y-6">
            <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
                <label htmlFor="website">Leave this field empty</label>
                <input
                    type="text"
                    id="website"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                />
            </div>
            {state.success && (
                <div className="p-4 rounded-md bg-green-50 text-green-700 flex items-start gap-3">
                    <CheckCircle className="shrink-0 mt-0.5" size={18} />
                    <div>
                        <p className="font-medium">Success!</p>
                        <p className="text-sm">{state.message}</p>
                    </div>
                </div>
            )}

            {!state.success && state.message && (
                <div className="p-4 rounded-md bg-red-50 text-red-700 flex items-start gap-3">
                    <AlertCircle className="shrink-0 mt-0.5" size={18} />
                    <div>
                        <p className="font-medium">Error</p>
                        <p className="text-sm">{state.message}</p>
                    </div>
                </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                    <label htmlFor="firstName" className="text-sm font-medium text-[var(--color-text-muted)]">First Name</label>
                    <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        required
                        className={`w-full rounded-md border bg-[var(--color-bg-base)] px-4 py-3 text-sm text-[var(--color-text-main)] outline-none transition-colors ${state.errors?.firstName ? 'border-red-500 focus:border-red-500' : 'border-[var(--color-border)] focus:border-[var(--color-primary)]'}`}
                        placeholder="John"
                    />
                    {state.errors?.firstName && <p className="text-xs text-red-500">{state.errors.firstName}</p>}
                </div>
                <div className="space-y-2">
                    <label htmlFor="lastName" className="text-sm font-medium text-[var(--color-text-muted)]">Last Name</label>
                    <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        required
                        className={`w-full rounded-md border bg-[var(--color-bg-base)] px-4 py-3 text-sm text-[var(--color-text-main)] outline-none transition-colors ${state.errors?.lastName ? 'border-red-500 focus:border-red-500' : 'border-[var(--color-border)] focus:border-[var(--color-primary)]'}`}
                        placeholder="Doe"
                    />
                    {state.errors?.lastName && <p className="text-xs text-red-500">{state.errors.lastName}</p>}
                </div>
            </div>

            <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-[var(--color-text-muted)]">Email Address</label>
                <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className={`w-full rounded-md border bg-[var(--color-bg-base)] px-4 py-3 text-sm text-[var(--color-text-main)] outline-none transition-colors ${state.errors?.email ? 'border-red-500 focus:border-red-500' : 'border-[var(--color-border)] focus:border-[var(--color-primary)]'}`}
                    placeholder="john@example.com"
                />
                {state.errors?.email && <p className="text-xs text-red-500">{state.errors.email}</p>}
            </div>

            <div className="space-y-2">
                <label htmlFor="message" className="text-sm font-medium text-[var(--color-text-muted)]">Message</label>
                <textarea
                    id="message"
                    name="message"
                    rows={4}
                    required
                    className={`w-full rounded-md border bg-[var(--color-bg-base)] px-4 py-3 text-sm text-[var(--color-text-main)] outline-none transition-colors ${state.errors?.message ? 'border-red-500 focus:border-red-500' : 'border-[var(--color-border)] focus:border-[var(--color-primary)]'}`}
                    placeholder="I'm interested in..."
                ></textarea>
                {state.errors?.message && <p className="text-xs text-red-500">{state.errors.message}</p>}
            </div>

            <button
                type="submit"
                disabled={isPending}
                className="w-full btn btn-primary py-3 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
                {isPending ? (
                    <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Sending...</span>
                    </>
                ) : (
                    <>
                        <Send size={18} />
                        <span>Send Message</span>
                    </>
                )}
            </button>
        </form>
    );
}
